import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Info, X } from 'lucide-react';
import { fetchJsonCached, getCachedJson } from '../utils/portalQueryCache';
import { getPlatformLogoSources, getPlatformScreenshotSources, openPlatformTool } from '../utils/platformNavigation';
import { platformIconSvgDataUrl } from '../utils/platformLauncherUtils';
import { TOOLS_CACHE_URL, usePlatformAccessChanged } from '../utils/platformAccessSync';
import { revealItemTransition } from '../utils/motionReveal';

function PlatformScreenshot({ tool }) {
  const sources = useMemo(() => getPlatformScreenshotSources(tool), [tool]);
  const [src, setSrc] = useState(() => sources?.primary || '');
  useEffect(() => {
    setSrc(sources?.primary || '');
  }, [sources?.primary]);
  if (!sources?.primary) return null;

  return (
    <div className="platform-card-media" aria-hidden="true">
      <img
        className="platform-card-screenshot"
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => {
          if (!sources?.fallback || src === sources.fallback) {
            return;
          }
          setSrc(sources.fallback);
        }}
      />
      <div className="platform-card-media-overlay" />
    </div>
  );
}

const PlatformInfoIcon = React.memo(function PlatformInfoIcon({ tool }) {
  const sources = useMemo(() => getPlatformLogoSources(tool), [tool]);
  const generatedSvg = useMemo(() => platformIconSvgDataUrl(tool), [tool]);
  const [src, setSrc] = useState(() => sources?.primary || generatedSvg);

  useEffect(() => {
    setSrc(sources?.primary || generatedSvg);
  }, [sources?.primary, generatedSvg]);

  return (
    <span className="platform-info-modal-icon" aria-hidden="true">
      <img
        src={src}
        alt=""
        width={56}
        height={56}
        decoding="async"
        loading="eager"
        onError={() => {
          if (src === generatedSvg) return;
          if (sources?.fallback && src !== sources.fallback) {
            setSrc(sources.fallback);
            return;
          }
          setSrc(generatedSvg);
        }}
      />
    </span>
  );
});

const PlatformInfoModal = React.memo(function PlatformInfoModal({ tool, onClose, onOpen }) {
  const shotSources = useMemo(() => getPlatformScreenshotSources(tool), [tool]);
  const shotSrc = shotSources?.primary || shotSources?.fallback || '';
  const [mediaSrc, setMediaSrc] = useState(shotSrc);
  const description = String(tool?.description || '').trim();

  useEffect(() => {
    setMediaSrc(shotSrc);
  }, [shotSrc]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown, { passive: true });
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  if (typeof document === 'undefined' || !tool) return null;

  return createPortal(
    <div className="platform-info-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="platform-info-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-info-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="platform-info-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="platform-info-modal-media">
          {mediaSrc ? (
            <img
              src={mediaSrc}
              alt=""
              width={960}
              height={480}
              decoding="async"
              fetchPriority="high"
              onError={() => {
                if (!shotSources?.fallback || mediaSrc === shotSources.fallback) return;
                setMediaSrc(shotSources.fallback);
              }}
            />
          ) : (
            <div className="platform-info-modal-media-fallback" />
          )}
        </div>

        <div className="platform-info-modal-body">
          <div className="platform-info-modal-heading">
            <PlatformInfoIcon tool={tool} />
            <div className="platform-info-modal-heading-text">
              <p className="platform-info-modal-kicker">Platform</p>
              <h3 id="platform-info-title">{tool.name}</h3>
            </div>
          </div>
          {description ? (
            <p className="platform-info-modal-description">{description}</p>
          ) : (
            <p className="platform-info-modal-description is-muted">No description provided.</p>
          )}
          <div className="platform-info-modal-actions">
            <button type="button" className="ghost-button" onClick={onClose}>
              Close
            </button>
            <button type="button" className="primary-button" onClick={() => onOpen(tool)}>
              Open platform
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
});

export function ToolsPage() {
  const navigate = useNavigate();
  const cachedTools = getCachedJson(TOOLS_CACHE_URL);
  const [tools, setTools] = useState(() => (Array.isArray(cachedTools?.tools) ? cachedTools.tools : []));
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(null);
  const [infoTool, setInfoTool] = useState(null);
  const openingTimerRef = useRef(0);
  const infoOpen = !!infoTool;

  const applyToolsPayload = useCallback((data) => {
    setTools(Array.isArray(data?.tools) ? data.tools : []);
  }, []);

  const reloadTools = useCallback(() => {
    fetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 60_000 })
      .then(applyToolsPayload)
      .catch(() => null);
  }, [applyToolsPayload]);

  usePlatformAccessChanged(reloadTools);

  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await fetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 60_000, signal: controller.signal });
        if (isCancelled) return;
        applyToolsPayload(data);
        setError('');
      } catch (e) {
        if (isCancelled) return;
        setError('Failed to load platforms list.');
      }
    };

    void load();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [applyToolsPayload]);

  useEffect(() => {
    const links = [];
    const addPreconnect = (origin) => {
      if (!origin) return;
      const href = String(origin).trim();
      if (!href) return;
      const existing = document.querySelector(`link[rel="preconnect"][href="${href}"]`);
      if (existing) return;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      links.push(link);
    };

    for (const tool of tools) {
      const rawUrl = String(tool?.url || '').trim();
      if (!rawUrl || (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://'))) continue;
      try {
        const u = new URL(rawUrl);
        addPreconnect(u.origin);
      } catch {
        // ignore malformed URLs from catalog
      }
    }

    return () => {
      for (const link of links) {
        if (link?.parentNode) {
          link.parentNode.removeChild(link);
        }
      }
    };
  }, [tools]);

  useEffect(() => {
    return () => {
      if (openingTimerRef.current) {
        window.clearTimeout(openingTimerRef.current);
      }
    };
  }, []);

  const openTool = useCallback((event, tool) => {
    if (!tool || opening) return;

    const areaUrls = Array.isArray(tool.dataAreaUrls) ? tool.dataAreaUrls : [];
    const hasAreaUrls = areaUrls.length > 0;
    const requiresPick = tool.requiresDataAreaPick === true || areaUrls.length > 1;
    const rawUrl = String(tool.url || '').trim();
    const url = rawUrl && !rawUrl.startsWith('http://') && !rawUrl.startsWith('https://') && !rawUrl.startsWith('/')
      ? `/${rawUrl}`
      : rawUrl;
    if (!url && !hasAreaUrls && !requiresPick) return;

    if (requiresPick || hasAreaUrls || url.startsWith('http://') || url.startsWith('https://')) {
      void openPlatformTool(tool, navigate);
      return;
    }

    const target = event?.currentTarget;
    const rect = target?.getBoundingClientRect?.();
    if (!rect) {
      void openPlatformTool(tool, navigate);
      return;
    }

    setOpening({
      tool,
      rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
    });
    openingTimerRef.current = window.setTimeout(() => {
      navigate(url);
    }, 60);
  }, [navigate, opening]);

  const closeInfoModal = useCallback(() => {
    setInfoTool(null);
  }, []);

  const onOpenFromInfo = useCallback((tool) => {
    setInfoTool(null);
    void openPlatformTool(tool, navigate);
  }, [navigate]);

  const openInfoModal = useCallback((tool) => {
    startTransition(() => {
      setInfoTool(tool);
    });
  }, []);

  const visibleTools = useMemo(() => tools.filter((t) => t && t.key), [tools]);

  if (error) {
    return <p className="error-text">{error}</p>;
  }

  return (
    <section className={infoOpen ? 'platforms-page is-info-open' : 'platforms-page'}>
      <motion.div
        className="section-heading"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
      >
        <div>
          <h2>Platforms</h2>
        </div>
      </motion.div>
      <div className="tools-grid">
        {visibleTools.map((tool, index) => (
          <motion.article
            key={tool.key}
            className="tool-card"
            role="button"
            tabIndex={0}
            onClick={(event) => openTool(event, tool)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openTool(event, tool);
              }
            }}
            initial={{ opacity: 0, y: -22, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={revealItemTransition(index, { duration: 0.42, step: 0.06, cap: 12 })}
          >
            <div className="tool-card-media-wrap">
              <PlatformScreenshot tool={tool} />
              <button
                type="button"
                className="tool-card-info-button"
                aria-label={`About ${tool.name}`}
                title="About this platform"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openInfoModal(tool);
                }}
                onPointerDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <Info size={15} strokeWidth={2.35} aria-hidden="true" />
              </button>
            </div>
            <div className="tool-card-body">
              <div className="tool-card-row">
                <h3 title={tool.name}>{tool.name}</h3>
              </div>
              <p title={tool.description || ''}>{tool.description}</p>
            </div>
          </motion.article>
        ))}
      </div>

      {infoTool ? (
        <PlatformInfoModal
          tool={infoTool}
          onClose={closeInfoModal}
          onOpen={onOpenFromInfo}
        />
      ) : null}

      {opening?.tool && opening?.rect && (
        <>
          <motion.div
            className="tool-open-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.div
            className="tool-open-expander"
            style={{
              left: opening.rect.left,
              top: opening.rect.top,
              width: opening.rect.width,
              height: opening.rect.height,
              transformOrigin: 'top left',
            }}
            initial={{ x: 0, y: 0, scaleX: 1, scaleY: 1, borderRadius: 26 }}
            animate={{
              x: -opening.rect.left,
              y: -opening.rect.top,
              scaleX: window.innerWidth / Math.max(1, opening.rect.width),
              scaleY: window.innerHeight / Math.max(1, opening.rect.height),
              borderRadius: 0,
            }}
            transition={{ duration: 0.12, ease: [0.22, 1, 0.36, 1] }}
          />
        </>
      )}
    </section>
  );
}
